import { useState, type ReactElement } from 'react';

import { ApiError, apiFetch } from '../lib/api';

/**
 * Cuts an invite and shows the link that carries it.
 *
 * Registration is invite-only, so this is the only way a second person ever
 * gets into the server. The link is built here rather than by the API because
 * only the browser knows which origin the app is being served from.
 *
 * It points at `/invite/<code>`, which serves both people the same code has to
 * serve: somebody who already has an account enters this server there without
 * creating a second one, and somebody who has none is offered the sign-up with
 * the same code by the login screen behind it.
 *
 * The sidebar renders this for administrators only; the route refuses anybody
 * else anyway, so the hidden button is convenience, not the permission.
 */

const GENERIC_FAILURE = 'Não foi possível gerar o convite. Tente de novo.';

export interface InviteButtonProps {
  /**
   * The server the code opens the door to. A code belongs to one server and to
   * no other, so which one it is is stated, never inferred.
   */
  guildId: string;
}

export function InviteButton({ guildId }: InviteButtonProps): ReactElement {
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function requestInvite(): Promise<void> {
    setPending(true);
    setError(null);

    try {
      // Only the server: the rest defaults to one use and no expiry.
      const { code } = await apiFetch<{ code: string }>('/api/invites', {
        method: 'POST',
        body: { guildId },
      });
      setLink(`${window.location.origin}/invite/${code}`);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="invite">
      <button
        type="button"
        className="invite-button"
        disabled={pending}
        onClick={() => {
          void requestInvite();
        }}
      >
        {pending ? 'Gerando…' : 'Gerar convite'}
      </button>

      {error !== null && (
        <p className="invite-error" role="alert">
          {error}
        </p>
      )}

      {link !== null && (
        <label className="invite-link">
          <span className="invite-link-label">Link de uso único</span>
          {/* Read-only and selected on focus: the link is meant to be copied,
              never typed over. */}
          <input
            readOnly
            value={link}
            onFocus={(event) => {
              event.currentTarget.select();
            }}
          />
        </label>
      )}
    </div>
  );
}

export default InviteButton;
